import { useEffect, useState } from 'react';

import { getI18n } from 'react-i18next';
import { fetchApi } from 'utils/fetchApi';

import { useUserOrganization } from '~/features/organizations/hooks/useUserOrganization';

// 👇 The Look ID for the Companion
const LOOKER_COMPANION_BY_LANGUAGE = {
  EN: 299,
  FR: 306,
  DE: 305,
  IT: 308,
  ES: 311,
  PT: 309,
};
// 👇 The default Look ID for the Companion
let LOOKER_COMPANION_REPORT_ID = LOOKER_COMPANION_BY_LANGUAGE['EN'];

export type CompanionState = {
  looker: {
    loading: boolean;
    url: string | null;
    error?: string;
  };
};
const initialState: CompanionState = {
  looker: {
    loading: false,
    url: null,
  },
};

/**
 * Handle business logic related to Learning Paths
 *
 * @returns LearningState
 */
export function useCompanion() {

  const { language } = getI18n();
  const [looker, setLooker] = useState<CompanionState['looker']>(initialState.looker);
  const LANG_KEY = language.split('_')[0].toUpperCase();
  const { organizationData, isLoading, error } = useUserOrganization();

  if (LOOKER_COMPANION_BY_LANGUAGE[LANG_KEY as keyof typeof LOOKER_COMPANION_BY_LANGUAGE]) {
    LOOKER_COMPANION_REPORT_ID = LOOKER_COMPANION_BY_LANGUAGE[LANG_KEY as keyof typeof LOOKER_COMPANION_BY_LANGUAGE];
  }

  useEffect(() => {
    let stale = false;
    async function fetchLookerURL(language: string) {
      setLooker({
        loading: true,
        url: null,
      });
      try {
        const result = await fetchApi(`/api/lookerDashboardUrlById?lang=${language}&id=${LOOKER_COMPANION_REPORT_ID}`, {
          method: 'GET',
        });
        const data = (await result.json()) as { url: string };
        if (!stale) {
          setLooker({
            loading: false,
            url: data.url,
          });
        }
      } catch (error) {
        console.error(`Failed to fetch Looker URL: ${error}`);
        if (!stale) {
          setLooker({
            loading: false,
            url: null,
            error: String(error),
          });
        }
      }
    }
    fetchLookerURL(language);

    return () => {
      stale = true;
    };
  }, [language]);

  return { org: organizationData, looker, isLoading, error };
}
